import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatedBackground } from '@/components/AnimatedBackground';
import {
  ArrowRight, Calendar, Clock, Compass, Layers, LayoutDashboard, Library, MoonStar, Orbit, RefreshCw, Sparkles,
} from 'lucide-react';
import { getToday13 } from '@/lib/calendar13';
import { MONTH_NAMES, PLASMA_NAMES } from '@/lib/calendar13Types';
import type { Day13 } from '@/lib/calendar13Types';

// Home space for thirteen.happyapps.dev — sits in front of /landing and features
// THIRTEEN itself: today's 13-moon position, the 13 moons, the 7 plasmas, and
// doors into each part of the app.

const SPACES = [
  {
    to: '/dashboard/thirteen',
    icon: Calendar,
    title: '13 Moon Calendar',
    body: 'The 13 × 28 grid with your events and mined timeline moments laid over each moon.',
  },
  {
    to: '/dashboard',
    icon: LayoutDashboard,
    title: 'Cycle Dashboard',
    body: 'Rings, widgets and skins — moons, seals, plasmas and space weather in one view.',
  },
  {
    to: '/collections',
    icon: Library,
    title: 'Collections',
    body: 'Your queue, saved snippets and history from channels on the timeline.',
  },
  {
    to: '/landing',
    icon: Compass,
    title: 'About THIRTEEN',
    body: 'The project overview, early access sign-up and what is coming next.',
  },
];

const PILLARS = [
  {
    icon: Clock,
    title: 'Truth layer first',
    body: 'Every event is a real UTC timestamp. The 13-month view is a projection of real time, never a replacement.',
  },
  {
    icon: MoonStar,
    title: 'Real lunar timing',
    body: 'Moon phases come from astronomical computation — no symbolic approximations or fake cycle math.',
  },
  {
    icon: Layers,
    title: 'Layered cycles',
    body: 'Moons, seals, colours and plasmas coexist as rings instead of being forced into one grid.',
  },
  {
    icon: RefreshCw,
    title: 'Sync-safe',
    body: 'Everything resolves back to Gregorian dates, so it exports to the calendars you already use.',
  },
];

const fmtGregorian = (d: Date) =>
  d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

// Re-read "today" every minute so the page rolls over at midnight without a reload.
function useToday13(): { today: Day13; now: Date } {
  const [state, setState] = useState(() => ({ today: getToday13(), now: new Date() }));
  useEffect(() => {
    const id = window.setInterval(() => setState({ today: getToday13(), now: new Date() }), 60_000);
    return () => window.clearInterval(id);
  }, []);
  return state;
}

const Home = () => {
  const { today, now } = useToday13();
  const outOfTime = Boolean(today.isOutOfTime);
  const moon = outOfTime ? null : MONTH_NAMES[today.month - 1];
  const plasma = outOfTime ? null : PLASMA_NAMES[(today.day - 1) % 7];
  const dayOfYear = outOfTime ? 365 : (today.month - 1) * 28 + today.day;
  const weekOfMoon = outOfTime ? 0 : Math.ceil(today.day / 7);
  const moonProgress = outOfTime ? 100 : Math.round((today.day / 28) * 100);

  return (
    <div className="min-h-screen bg-background nebula-bg relative overflow-x-hidden">
      <AnimatedBackground />

      <div className="relative z-10">
        <header className="mx-auto flex w-full max-w-6xl items-center justify-between gap-3 px-4 py-5">
          <Link to="/" className="flex items-center gap-2 font-display text-lg font-bold tracking-wide">
            <span className="text-cosmic-gradient text-2xl">13</span>
            <span>THIRTEEN</span>
          </Link>
          <nav className="flex flex-wrap items-center justify-end gap-2 text-sm">
            <Link to="/landing" className="hidden rounded-lg px-3 py-2 text-muted-foreground hover:text-foreground sm:inline-flex">
              About
            </Link>
            <Link to="/dashboard/thirteen" className="hidden rounded-lg px-3 py-2 text-muted-foreground hover:text-foreground sm:inline-flex">
              Calendar
            </Link>
            <Link to="/beta" className="rounded-lg border border-border/60 bg-card/60 px-3 py-2 backdrop-blur-sm hover:border-primary/40">
              Sign in
            </Link>
          </nav>
        </header>

        <main className="mx-auto w-full max-w-6xl px-4 pb-20">
          {/* Hero + today */}
          <section className="grid grid-cols-1 items-center gap-10 py-8 md:py-14 lg:grid-cols-[1.2fr_1fr]">
            <div className="animate-fade-in">
              <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-card/60 px-4 py-2 text-xs uppercase tracking-[0.3em] text-muted-foreground">
                <Sparkles className="h-3.5 w-3.5 text-primary" />
                Home of THIRTEEN
              </div>
              <h1 className="mt-6 text-4xl font-display font-bold leading-tight sm:text-5xl lg:text-6xl">
                Time in <span className="text-cosmic-gradient">13 moons</span>, anchored to the real clock.
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-8 text-muted-foreground">
                THIRTEEN is a galactic calendar: 13 moons of 28 days plus a Day Out of Time, with real lunar phases and
                layered cycles over the dates you already live by.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/dashboard/thirteen"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3 font-medium text-primary-foreground cosmic-glow-sm transition-transform duration-300 hover:-translate-y-0.5"
                >
                  Open the Calendar
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/landing"
                  className="inline-flex items-center justify-center rounded-xl border border-border/60 bg-card/60 px-6 py-3 font-medium backdrop-blur-sm transition-colors hover:border-primary/40 hover:bg-card"
                >
                  Learn about THIRTEEN
                </Link>
              </div>
            </div>

            <div
              className="animate-fade-in overflow-hidden rounded-[28px] border border-border/60 bg-card/65 shadow-2xl shadow-primary/10 backdrop-blur-md"
              style={{ animationDelay: '0.15s' }}
            >
              <div className="border-b border-border/50 px-6 py-5">
                <p className="text-xs uppercase tracking-[0.35em] text-muted-foreground">Today in THIRTEEN</p>
                <p className="mt-1 text-sm text-muted-foreground">{fmtGregorian(now)}</p>
              </div>
              <div className="px-6 py-6">
                {outOfTime ? (
                  <div>
                    <div className="text-4xl font-display font-bold text-cosmic-gradient">Day Out of Time</div>
                    <p className="mt-3 text-sm text-muted-foreground">
                      The day between years — outside the 13 moons, before the new galactic year begins.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="flex items-end gap-4">
                      <div className="text-6xl leading-none" aria-hidden>{moon?.glyph}</div>
                      <div>
                        <div className="text-sm uppercase tracking-[0.25em] text-muted-foreground">Moon {today.month}</div>
                        <div className="text-3xl font-display font-bold">{moon?.name} Moon</div>
                      </div>
                    </div>
                    <div className="mt-6 grid grid-cols-3 gap-3 text-center">
                      <div className="rounded-xl border border-border/50 bg-background/50 p-3">
                        <div className="text-2xl font-display text-cosmic-gradient">{today.day}</div>
                        <div className="mt-1 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Day / 28</div>
                      </div>
                      <div className="rounded-xl border border-border/50 bg-background/50 p-3">
                        <div className="text-2xl font-display text-cosmic-gradient">{weekOfMoon}</div>
                        <div className="mt-1 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Week / 4</div>
                      </div>
                      <div className="rounded-xl border border-border/50 bg-background/50 p-3">
                        <div className="text-2xl font-display text-cosmic-gradient">{plasma?.name}</div>
                        <div className="mt-1 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Plasma</div>
                      </div>
                    </div>
                    <div className="mt-6">
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>Moon progress</span>
                        <span>{moonProgress}%</span>
                      </div>
                      <div className="mt-2 h-2 overflow-hidden rounded-full bg-background/60">
                        <div className="h-full rounded-full bg-primary" style={{ width: `${moonProgress}%` }} />
                      </div>
                    </div>
                  </>
                )}
                <p className="mt-5 text-xs text-muted-foreground">
                  Day {dayOfYear} of 365 · galactic year {today.year}
                </p>
              </div>
            </div>
          </section>

          {/* The 13 moons */}
          <section className="py-10">
            <div className="flex items-end justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.35em] text-muted-foreground">The year</p>
                <h2 className="mt-2 text-3xl font-display font-bold">13 moons × 28 days</h2>
              </div>
              <Link to="/dashboard/thirteen" className="hidden items-center gap-1 text-sm text-primary hover:underline sm:inline-flex">
                See the grid <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
              {MONTH_NAMES.map((m) => {
                const isCurrent = !outOfTime && m.number === today.month;
                const isPast = outOfTime || m.number < today.month;
                return (
                  <div
                    key={m.number}
                    className={`rounded-2xl border p-4 backdrop-blur-sm transition-colors ${
                      isCurrent
                        ? 'border-primary/70 bg-primary/15 cosmic-glow-sm'
                        : isPast
                          ? 'border-border/40 bg-card/35 opacity-70'
                          : 'border-border/60 bg-card/55'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">{m.number}</span>
                      <span className="text-xl" aria-hidden>{m.glyph}</span>
                    </div>
                    <div className="mt-3 font-medium">{m.name}</div>
                    {isCurrent && <div className="mt-1 text-[11px] uppercase tracking-[0.2em] text-primary">Now</div>}
                  </div>
                );
              })}
              <div
                className={`rounded-2xl border border-dashed p-4 ${
                  outOfTime ? 'border-primary/70 bg-primary/15' : 'border-border/60 bg-card/30'
                }`}
              >
                <div className="text-xs text-muted-foreground">+1</div>
                <div className="mt-3 font-medium">Day Out of Time</div>
                {outOfTime && <div className="mt-1 text-[11px] uppercase tracking-[0.2em] text-primary">Now</div>}
              </div>
            </div>
          </section>

          {/* The 7 plasmas */}
          <section className="py-10">
            <p className="text-xs uppercase tracking-[0.35em] text-muted-foreground">The week</p>
            <h2 className="mt-2 text-3xl font-display font-bold">7 plasmas, four times a moon</h2>
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
              {PLASMA_NAMES.map((p) => {
                const isCurrent = plasma?.name === p.name;
                return (
                  <div
                    key={p.name}
                    className={`rounded-2xl border p-4 backdrop-blur-sm ${
                      isCurrent ? 'border-accent/70 bg-accent/15' : 'border-border/60 bg-card/55'
                    }`}
                  >
                    <div className="font-display text-lg">{p.name}</div>
                    <div className="mt-1 text-xs text-muted-foreground">{p.chakra}</div>
                    {isCurrent && <div className="mt-2 text-[11px] uppercase tracking-[0.2em] text-accent">Today</div>}
                  </div>
                );
              })}
            </div>
          </section>

          {/* What THIRTEEN stands on */}
          <section className="py-10">
            <p className="text-xs uppercase tracking-[0.35em] text-muted-foreground">Principles</p>
            <h2 className="mt-2 text-3xl font-display font-bold">Calm rhythm, real time</h2>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {PILLARS.map(({ icon: Icon, title, body }) => (
                <div key={title} className="rounded-2xl border border-border/60 bg-card/55 p-5 backdrop-blur-sm">
                  <Icon className="h-5 w-5 text-primary" />
                  <div className="mt-3 font-medium">{title}</div>
                  <p className="mt-2 text-sm text-muted-foreground">{body}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Spaces */}
          <section className="py-10">
            <p className="text-xs uppercase tracking-[0.35em] text-muted-foreground">Explore</p>
            <h2 className="mt-2 text-3xl font-display font-bold">Spaces in THIRTEEN</h2>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {SPACES.map(({ to, icon: Icon, title, body }) => (
                <Link
                  key={to}
                  to={to}
                  className="group flex items-start gap-4 rounded-2xl border border-border/60 bg-card/55 p-5 backdrop-blur-sm transition-colors hover:border-primary/40 hover:bg-card"
                >
                  <Icon className="mt-0.5 h-6 w-6 shrink-0 text-primary" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 font-medium">
                      {title}
                      <ArrowRight className="h-4 w-4 opacity-0 transition-opacity group-hover:opacity-100" />
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">{body}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        </main>

        <footer className="border-t border-border/40">
          <div className="mx-auto flex w-full max-w-6xl flex-col gap-3 px-4 py-6 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <Orbit className="h-4 w-4" /> THIRTEEN · thirteen.happyapps.dev
            </div>
            <div className="flex flex-wrap gap-4">
              <Link to="/landing" className="hover:text-foreground">About</Link>
              <Link to="/plans" className="hover:text-foreground">Plans</Link>
              <Link to="/beta" className="hover:text-foreground">Sign in</Link>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Home;
